import { useQuery } from '@tanstack/react-query';

import { instanceV2 } from '@/api/instance';

import type { RemoteConfigData } from './dto';

export const useInhouseRemoteConfig = <T = any>(
  key: string,
  parser: (value: string) => T,
  enabled: boolean = true,
): { value: T | null; isLoading: boolean; error: Error | null } => {
  const { data, isLoading, error } = useQuery<RemoteConfigData>({
    queryKey: ['remote-config', key],
    queryFn: async () => {
      // 한글 key를 위한 URL encoding
      const encodedKey = encodeURIComponent(key);
      const response = await instanceV2.get<RemoteConfigData>(
        `/v3/remote-configs/${encodedKey}`,
      );
      return response;
    },
    enabled: !!key && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  if (isLoading) {
    return { value: null, isLoading: true, error: null };
  }
  if (error) {
    return { value: null, isLoading: false, error };
  }

  try {
    const value = data?.value;
    return {
      value: value ? parser(value) : null,
      isLoading: false,
      error: null,
    };
  } catch (parseError) {
    return {
      value: null,
      isLoading: false,
      error: parseError as Error,
    };
  }
};
